import {
    AREA_DECIMAL_PLACES,
    COVERAGE_FRACTION_PERCENT_MULTIPLIER,
    MILES_DECIMAL_PLACES,
    PERCENT_DECIMAL_PLACES
} from '../../constants';
import { hectaresToAcres } from 'utils/areaUtil';
import { formatMinutesSeconds } from 'utils/dateTimeUtils/durationUtil';
import { metersToMiles } from 'utils/distanceUtil';

import type { UseDisplayValuesParams, UseDisplayValuesReturn } from './types';

const formatNumber = ({ decimals, value }: { decimals: number; value: number }): string =>
    value.toLocaleString(undefined, {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals
    });

const useDisplayValues = ({
    boundaryAreaHectares,
    placeholderValue,
    speedMetersPerSecond,
    stats
}: UseDisplayValuesParams): UseDisplayValuesReturn => {
    const totalDistance = stats
        ? formatNumber({
              decimals: MILES_DECIMAL_PLACES,
              value: metersToMiles(stats.totalDistanceM)
          })
        : placeholderValue;
    const productive = stats
        ? formatNumber({
              decimals: PERCENT_DECIMAL_PLACES,
              value: stats.productiveRatio * COVERAGE_FRACTION_PERCENT_MULTIPLIER
          })
        : placeholderValue;
    const passes = stats ? `${stats.numPasses}` : placeholderValue;
    const turns = stats ? `${stats.numTurns}` : placeholderValue;
    const coverageArea =
        boundaryAreaHectares !== null
            ? formatNumber({
                  decimals: AREA_DECIMAL_PLACES,
                  value: hectaresToAcres(boundaryAreaHectares)
              })
            : placeholderValue;
    // Flight time needs a positive speed to be meaningful; guard against zero/invalid
    // speed so we show the placeholder instead of dividing by zero.
    const canComputeFlightTime = stats !== null && speedMetersPerSecond > 0;
    const flightTime = canComputeFlightTime
        ? formatMinutesSeconds(stats.totalDistanceM / speedMetersPerSecond)
        : placeholderValue;

    return {
        coverageArea,
        flightTime,
        passes,
        productive,
        totalDistance,
        turns
    };
};

export default useDisplayValues;
